// safety/incidents-and-injuries/components/tabs/AppointmentsTab.tsx
import React, { useState } from 'react';
import { Calendar, Trash2 } from 'lucide-react';
import { SectionTitle, AddButton, FormModal } from '../shared';
import { fmtDate, capacityColor, todayISO } from '../../utils/incidents-utils';
import type { Incident, Appointment, SafetyLists } from '../../../../../types/incident.types';

export const AppointmentsTab: React.FC<{
    incident: Incident;
    lists: SafetyLists;
    onAdd: (a: Appointment) => void;
    onDelete: (id: string) => void;
}> = ({ incident: r, lists, onAdd, onDelete }) => {
    const [modal, setModal] = useState(false);
    const next = [...r.appointments].sort((a, b) => (a.date < b.date ? 1 : -1))[0];

    return (
        <>
            <SectionTitle title="Medical Appointments" right={<AddButton label="Add Appointment" onClick={() => setModal(true)} />} />

            {next && (
                <div className="rounded-lg border border-border bg-accent/40 px-4 py-3 mb-4 flex items-center gap-2 text-sm">
                    <Calendar size={16} className="text-muted-foreground" />
                    <span>
                        Next appointment: <span className="font-medium text-primary">{fmtDate(next.date)}</span> – {next.doctor} at {next.clinic}
                    </span>
                </div>
            )}

            <div className="rounded-lg border border-border overflow-hidden">
                <table className="w-full text-sm">
                    <thead className="bg-muted/30 border-b border-border">
                        <tr>
                            {['Date', 'Doctor', 'Clinic', 'Outcome', 'Notes', ''].map((h) => (
                                <th key={h} className="text-left font-medium text-[11px] uppercase tracking-wide text-muted-foreground px-3 py-2.5">
                                    {h}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {r.appointments.length === 0 && (
                            <tr>
                                <td colSpan={6} className="px-3 py-8 text-center text-sm text-muted-foreground">
                                    No appointments yet.
                                </td>
                            </tr>
                        )}
                        {r.appointments.map((a) => (
                            <tr key={a.id} className="border-b border-border last:border-0">
                                <td className="px-3 py-3 font-medium whitespace-nowrap">{fmtDate(a.date)}</td>
                                <td className="px-3 py-3 text-muted-foreground">{a.doctor}</td>
                                <td className="px-3 py-3 text-muted-foreground">{a.clinic}</td>
                                <td className="px-3 py-3">
                                    <span className={`${capacityColor(a.outcome)} font-medium text-xs px-2 py-1 rounded-md bg-muted/60`}>{a.outcome}</span>
                                </td>
                                <td className="px-3 py-3 text-muted-foreground">{a.notes}</td>
                                <td className="px-3 py-3 text-right">
                                    <button onClick={() => onDelete(a.id)} className="text-muted-foreground hover:text-destructive">
                                        <Trash2 size={16} />
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {modal && (
                <FormModal
                    title="Add Appointment"
                    fields={[
                        { key: 'date', label: 'Date', type: 'date', value: todayISO() },
                        { key: 'doctor', label: 'Doctor', value: '' },
                        { key: 'clinic', label: 'Clinic', value: '' },
                        { key: 'outcome', label: 'Outcome', type: 'select', options: lists.capacityStatus, value: 'Light Duties' },
                        { key: 'notes', label: 'Notes', type: 'textarea', value: '' },
                    ]}
                    onClose={() => setModal(false)}
                    onSubmit={(v) => {
                        onAdd(v as any);
                        setModal(false);
                    }}
                />
            )}
        </>
    );
};
